import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["controls", "device", "addressToggle", "coordinateToggle", "addressPanel", "coordinatePanel", "query", "searchButton", "results", "latitude", "longitude", "summary", "map", "status"];
  static values = { url: String };

  connect() {
    this.sequence = 0;
    this.controlsTarget.hidden = false;
    this.coordinatePanelTarget.hidden = true;
    this.form = this.element.closest("form");
    this.beforeSubmit = event => {
      if (!this.deviceTarget.disabled && !this.searchButtonTarget.disabled) return;
      event.preventDefault();
      this.statusTarget.textContent = "Wait for the location lookup to finish, or choose LON × LAT to set the pin yourself.";
    };
    this.form?.addEventListener("submit", this.beforeSubmit);
    this.refreshMap();
  }

  disconnect() { this.cancel(); this.form?.removeEventListener("submit", this.beforeSubmit); }

  cancel() {
    this.sequence = (this.sequence || 0) + 1;
    this.request?.abort();
    this.request = null;
    clearTimeout(this.timeout);
    this.deviceTarget.disabled = false;
    this.searchButtonTarget.disabled = false;
    this.deviceTarget.textContent = "📍 Use my location";
    this.element.setAttribute("aria-busy", "false");
  }

  panels(mode) {
    this.addressPanelTarget.hidden = mode !== "address";
    this.coordinatePanelTarget.hidden = mode !== "coordinates";
    this.addressToggleTarget.setAttribute("aria-expanded", String(mode === "address"));
    this.coordinateToggleTarget.setAttribute("aria-expanded", String(mode === "coordinates"));
  }

  address(event) {
    event?.preventDefault();
    this.cancel();
    this.panels("address");
    this.statusTarget.textContent = "Search an address, then choose the matching worksite below.";
    this.queryTarget.focus();
  }

  coordinates(event) {
    event?.preventDefault();
    this.cancel();
    const opening = this.coordinatePanelTarget.hidden;
    this.panels(opening ? "coordinates" : null);
    this.queryTarget.setCustomValidity("");
    this.resultsTarget.replaceChildren();
    this.statusTarget.textContent = opening ? "Fine-tune the worksite pin. Longitude comes first, then latitude." : "";
    if (opening) this.longitudeTarget.focus();
  }

  invalidCoordinate() {
    this.cancel();
    this.panels("coordinates");
    this.queryTarget.setCustomValidity("");
    this.statusTarget.textContent = "Choose a location or enter valid longitude and latitude before saving.";
  }

  validPoint(latitude, longitude) {
    return [latitude, longitude].every(value => typeof value === "number" || typeof value === "string") &&
      String(latitude).trim() !== "" && String(longitude).trim() !== "" &&
      Number.isFinite(Number(latitude)) && Number.isFinite(Number(longitude)) &&
      Math.abs(Number(latitude)) <= 90 && Math.abs(Number(longitude)) <= 180;
  }

  refreshMap() {
    const latitude = this.latitudeTarget.value;
    const longitude = this.longitudeTarget.value;
    const valid = this.validPoint(latitude, longitude);
    this.mapTarget.hidden = !valid;
    if (valid) {
      const lat = Number(latitude).toFixed(7), lon = Number(longitude).toFixed(7);
      this.mapTarget.href = `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=18/${lat}/${lon}`;
    } else this.mapTarget.removeAttribute("href");
  }

  setPoint(latitude, longitude, label) {
    if (!this.validPoint(latitude, longitude)) throw new Error("The location was invalid. Please try again.");
    this.latitudeTarget.value = Number(latitude).toFixed(7);
    this.longitudeTarget.value = Number(longitude).toFixed(7);
    this.queryTarget.setCustomValidity("");
    this.summaryTarget.textContent = label;
    this.refreshMap();
  }

  manualChanged() {
    this.cancel();
    this.queryTarget.setCustomValidity("");
    this.resultsTarget.replaceChildren();
    this.summaryTarget.textContent = this.validPoint(this.latitudeTarget.value, this.longitudeTarget.value) ? "Manual worksite pin ready. Save the team to apply it." : "Enter a valid longitude and latitude.";
    this.statusTarget.textContent = "";
    this.refreshMap();
  }

  locate(event) {
    event?.preventDefault();
    this.cancel();
    this.panels(null);
    this.queryTarget.setCustomValidity("");
    this.resultsTarget.replaceChildren();
    if (!navigator.geolocation) {
      this.statusTarget.textContent = "This browser cannot provide location. Choose Address or LON × LAT.";
      return;
    }
    const sequence = this.sequence;
    this.deviceTarget.disabled = true;
    this.deviceTarget.textContent = "Finding your location…";
    this.element.setAttribute("aria-busy", "true");
    this.statusTarget.textContent = "Allow location access. Stay at the worksite while your phone gets a fresh reading.";
    navigator.geolocation.getCurrentPosition(position => {
      if (sequence !== this.sequence || !this.element.isConnected) return;
      this.cancel();
      const { latitude, longitude, accuracy } = position.coords;
      if (!this.validPoint(latitude, longitude) || !Number.isFinite(accuracy) || accuracy <= 0 || !Number.isFinite(position.timestamp) || Math.abs(Date.now() - position.timestamp) > 60000) {
        this.statusTarget.textContent = "A fresh, valid location was not received. Try again, or choose Address or LON × LAT.";
        return;
      }
      this.setPoint(latitude, longitude, "Device location ready. Save the team to apply it.");
      this.statusTarget.textContent = `Reported accuracy: ±${Math.ceil(accuracy)} m. Check the pin on the map; retry outdoors or fine-tune with LON × LAT if needed.`;
    }, error => {
      if (sequence !== this.sequence || !this.element.isConnected) return;
      this.cancel();
      const messages = {
        1: "Location permission was denied. Enable it for this site, or use Address or LON × LAT.",
        2: "Your location is unavailable. Try a clearer signal, or use Address or LON × LAT.",
        3: "Location timed out. Retry, or use Address or LON × LAT."
      };
      this.statusTarget.textContent = messages[error.code] || "Location could not be read. Use Address or LON × LAT.";
    }, { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 });
  }

  queryChanged() {
    this.cancel();
    this.resultsTarget.replaceChildren();
    this.queryTarget.setCustomValidity(this.queryTarget.value.trim() ? "Find this address and choose a result, or use device location or LON × LAT." : "");
    this.statusTarget.textContent = "";
  }

  async search(event) {
    event?.preventDefault();
    if (event?.isComposing) return;
    this.cancel();
    const query = this.queryTarget.value.trim();
    this.resultsTarget.replaceChildren();
    this.queryTarget.setCustomValidity("Find this address and choose a result, or use device location or LON × LAT.");
    if (query.length < 4 || query.length > 300) {
      this.statusTarget.textContent = "Enter a street address and city (4–300 characters).";
      return;
    }
    const sequence = this.sequence;
    this.request = new AbortController();
    this.timeout = setTimeout(() => this.request?.abort(), 10000);
    this.searchButtonTarget.disabled = true;
    this.element.setAttribute("aria-busy", "true");
    this.statusTarget.textContent = "Looking up the address…";
    try {
      const response = await fetch(this.urlValue, {
        method: "POST", credentials: "same-origin", signal: this.request.signal,
        headers: { "Content-Type": "application/json", "Accept": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || "" },
        body: JSON.stringify({ worksite_query: query })
      });
      const data = await response.json();
      if (sequence !== this.sequence || !this.element.isConnected) return;
      if (!response.ok) throw new Error(typeof data.error === "string" ? data.error : "Address search failed. Please retry.");
      if (!Array.isArray(data.results)) throw new Error("Address search returned an unreadable response. Please retry.");
      const results = data.results.slice(0, 5).filter(result => result && typeof result.label === "string" && this.validPoint(result.latitude, result.longitude));
      this.statusTarget.textContent = results.length ? "Choose your worksite. Check the map before saving." : "No matching address found. Add the city or postal code, or use your device location.";
      results.forEach(result => {
        const item = document.createElement("li"), button = document.createElement("button");
        button.type = "button";
        button.textContent = result.label;
        button.addEventListener("click", () => {
          this.cancel();
          this.setPoint(result.latitude, result.longitude, result.label);
          this.queryTarget.value = result.label.slice(0, 300);
          this.resultsTarget.replaceChildren();
          this.statusTarget.textContent = "Address selected. Check the pin on the map, then save the team.";
        });
        item.append(button); this.resultsTarget.append(item);
      });
    } catch (error) {
      if (sequence !== this.sequence || !this.element.isConnected) return;
      this.statusTarget.textContent = error.name === "AbortError" ? "Address search timed out. Retry, or use device location or LON × LAT." : error.message;
    } finally {
      if (sequence === this.sequence) this.cancel();
    }
  }
}
